import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import OverrideModal from "@/components/OverrideModal";
import ManualAmbient from "@/components/ManualAmbient";
import {
  Sun,
  Moon,
  CalendarCheck,
  Lightning,
  PauseCircle,
  PlayCircle,
  ArrowsClockwise,
  CircleNotch,
  WarningCircle,
  CheckCircle,
  PencilSimple,
} from "@phosphor-icons/react";

function formatTime(iso, tz = "Europe/Madrid") {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: tz,
    });
  } catch {
    return iso;
  }
}

function formatDate(iso, tz = "Europe/Madrid") {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      timeZone: tz,
    });
  } catch {
    return iso;
  }
}

function MetricCard({ label, value, sublabel, icon: Icon, color = "text-[#CCFF00]" }) {
  return (
    <div className="bg-[#121212] border border-zinc-800 rounded-md p-5 hover:border-zinc-700 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] text-zinc-500 tracking-[0.2em] uppercase font-mono">{label}</div>
        <Icon size={18} className={color} weight="bold" />
      </div>
      <div className="font-display text-3xl font-black tracking-tight">{value}</div>
      {sublabel && <div className="text-xs text-zinc-500 mt-1 font-mono">{sublabel}</div>}
    </div>
  );
}

function CourtCard({ court, booking, onToggle, toggling }) {
  const isOn = court.current_state === "on";
  return (
    <div
      className={`bg-[#121212] border rounded-md p-4 transition-all ${
        isOn ? "border-[#CCFF00]/40 glow-green" : "border-zinc-800 hover:border-zinc-700"
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-display font-bold text-lg leading-tight">{court.name}</div>
          <div className="text-[10px] text-zinc-500 font-mono tracking-wider uppercase mt-0.5">
            {court.switch_code}
          </div>
        </div>
        <div
          className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold tracking-wider ${
            isOn
              ? "bg-[#CCFF00] text-black"
              : "bg-zinc-900 text-zinc-500 border border-zinc-800"
          }`}
        >
          {isOn ? "ON" : "OFF"}
        </div>
      </div>

      <div className="text-xs text-zinc-400 mb-4 min-h-[36px]">
        {booking ? (
          <>
            <div className="text-zinc-500 text-[10px] uppercase tracking-wider font-mono mb-1">
              Próxima reserva
            </div>
            <div className="font-mono">
              {formatTime(booking.start)} – {formatTime(booking.end)}
            </div>
          </>
        ) : (
          <div className="text-zinc-600 italic">Sin próximas reservas</div>
        )}
      </div>

      <button
        data-testid={`court-toggle-${court.id}`}
        onClick={() => onToggle(court.id, !isOn)}
        disabled={toggling === court.id}
        className={`w-full py-2 rounded text-xs font-bold tracking-wider uppercase transition-all active:scale-[0.97] ${
          isOn
            ? "bg-zinc-900 text-zinc-300 hover:bg-zinc-800 border border-zinc-800"
            : "bg-[#CCFF00] text-black hover:bg-[#B3E600]"
        } disabled:opacity-50`}
      >
        {toggling === court.id ? (
          <CircleNotch size={14} className="inline animate-spin" />
        ) : isOn ? (
          "Apagar"
        ) : (
          "Encender"
        )}
      </button>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);

  const load = async () => {
    try {
      const d = await api.dashboard();
      setData(d);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.syncNow();
      // Poll until background sync finishes (max 90s)
      const startedAt = Date.now();
      while (Date.now() - startedAt < 90000) {
        await new Promise((r) => setTimeout(r, 2000));
        const s = await api.syncStatus();
        if (!s.running) {
          if (s.last_status === "error" && s.last_error) {
            alert("Sync completado con error: " + s.last_error);
          }
          break;
        }
      }
      await load();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleToggle = async (courtId, turnOn) => {
    setToggling(courtId);
    try {
      await api.toggleCourt(courtId, turnOn);
      await load();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setToggling(null);
    }
  };

  const handlePauseToggle = async () => {
    setPauseLoading(true);
    try {
      await api.updateSettings({ automation_paused: !data.automation_paused });
      await load();
    } finally {
      setPauseLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <CircleNotch size={32} className="animate-spin text-zinc-600" />
      </div>
    );
  }

  if (!data) return null;

  // Build next-booking map per court.
  // A booking is "current" until its lights turn off (or, if no lights are
  // scheduled e.g. diurnal, until the booking itself ends).
  const isBookingActive = (b, ref) => {
    const endRef = b.light_off_at || b.end;
    if (!endRef) return false;
    return new Date(endRef) >= ref;
  };

  const nextByCourt = {};
  const now = new Date(data.now);
  for (const b of data.bookings) {
    if (!b.court_id) continue;
    if (!isBookingActive(b, now)) continue;
    if (!nextByCourt[b.court_id]) {
      nextByCourt[b.court_id] = b;
    }
  }

  const upcomingBookings = data.bookings.filter((b) => isBookingActive(b, now)).slice(0, 12);
  const automationOn = !data.automation_paused;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] text-zinc-500 tracking-[0.3em] uppercase font-mono mb-1">
            Control Room
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
            Panel de control
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="pause-toggle-button"
            onClick={handlePauseToggle}
            disabled={pauseLoading}
            className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-bold transition-colors ${
              automationOn
                ? "bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800"
                : "bg-orange-500/10 border border-orange-500/30 text-orange-400 hover:bg-orange-500/20"
            }`}
          >
            {automationOn ? (
              <>
                <PauseCircle size={16} weight="fill" />
                Pausar automatización
              </>
            ) : (
              <>
                <PlayCircle size={16} weight="fill" />
                Reanudar automatización
              </>
            )}
          </button>
          <button
            data-testid="sync-now-button"
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 rounded text-sm font-bold bg-[#CCFF00] text-black hover:bg-[#B3E600] active:scale-[0.97] transition-all disabled:opacity-50"
          >
            <ArrowsClockwise size={16} weight="bold" className={syncing ? "animate-spin" : ""} />
            {syncing ? "Sincronizando..." : "Sincronizar"}
          </button>
        </div>
      </div>

      {!automationOn && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-md p-4 flex items-center gap-3">
          <WarningCircle size={20} className="text-orange-400" weight="fill" />
          <div className="text-sm">
            <span className="text-orange-300 font-bold">Automatización pausada.</span>{" "}
            <span className="text-orange-200/70">Las luces no se encenderán/apagarán automáticamente.</span>
          </div>
        </div>
      )}

      {/* Metrics row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          label="Anochecer hoy"
          value={formatTime(data.sunset_today)}
          sublabel={formatDate(data.sunset_today)}
          icon={Sun}
        />
        <MetricCard
          label="Amanecer mañana"
          value={formatTime(data.sunrise_tomorrow)}
          sublabel={formatDate(data.sunrise_tomorrow)}
          icon={Moon}
          color="text-cyan-400"
        />
        <MetricCard
          label="Reservas próximas"
          value={upcomingBookings.length}
          sublabel="Próximos 7 días"
          icon={CalendarCheck}
        />
        <MetricCard
          label="Eventos programados"
          value={data.scheduled_events_count}
          sublabel="En cola APScheduler"
          icon={Lightning}
        />
        <MetricCard
          label="Pistas activas"
          value={data.courts.filter((c) => c.current_state === "on").length}
          sublabel={`de ${data.courts.length} pistas`}
          icon={CheckCircle}
          color={
            data.courts.some((c) => c.current_state === "on") ? "text-[#CCFF00]" : "text-zinc-600"
          }
        />
      </div>

      {/* Courts grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-xl">Pistas</h2>
          <div className="text-xs text-zinc-500 font-mono">
            {data.courts.length} interruptores · Tuya 8-Gang
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          {data.courts.map((court) => (
            <CourtCard
              key={court.id}
              court={court}
              booking={nextByCourt[court.id]}
              onToggle={handleToggle}
              toggling={toggling}
            />
          ))}
        </div>
      </div>

      {/* Upcoming bookings list */}
      <div>
        <h2 className="font-display font-bold text-xl mb-4">Próximas reservas</h2>
        {upcomingBookings.length === 0 ? (
          <div className="bg-[#121212] border border-zinc-800 rounded-md p-8 text-center text-zinc-500">
            <CalendarCheck size={32} className="mx-auto mb-2 opacity-40" weight="bold" />
            <div className="text-sm">No hay reservas próximas. ¿Has configurado el Tenant ID de Playtomic?</div>
          </div>
        ) : (
          <div className="bg-[#121212] border border-zinc-800 rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-black/40 border-b border-zinc-800">
                <tr>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-500 tracking-wider uppercase font-mono">Día</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-500 tracking-wider uppercase font-mono">Pista</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-500 tracking-wider uppercase font-mono">Reserva</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-500 tracking-wider uppercase font-mono">Luces</th>
                  <th className="text-right px-4 py-3 text-[10px] text-zinc-500 tracking-wider uppercase font-mono"></th>
                </tr>
              </thead>
              <tbody>
                {upcomingBookings.map((b, idx) => (
                  <tr key={idx} className="border-b border-zinc-900 hover:bg-zinc-900/40 transition-colors">
                    <td className="px-4 py-3 text-sm font-mono text-zinc-300">{formatDate(b.start)}</td>
                    <td className="px-4 py-3 text-sm font-bold">
                      {b.resource_name}
                      {!b.court_id && (
                        <span className="ml-2 text-[10px] text-orange-400 font-mono">SIN MAPEAR</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-zinc-300">
                      {formatTime(b.start)} – {formatTime(b.end)}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono">
                      {b.override_disabled ? (
                        <span className="inline-flex items-center gap-1 text-xs text-red-300 bg-red-950/30 border border-red-900/40 px-2 py-0.5 rounded font-bold">
                          🚫 ENCENDIDO CANCELADO
                        </span>
                      ) : b.skipped_reason ? (
                        <span className="text-zinc-500 text-xs">{b.skipped_reason}</span>
                      ) : (
                        <span className="text-[#CCFF00] flex items-center gap-1">
                          ON {formatTime(b.light_on_at)} · OFF {formatTime(b.light_off_at)}
                          {b.override_applied && (
                            <span className="ml-1 text-[9px] bg-[#CCFF00]/20 px-1.5 py-0.5 rounded text-[#CCFF00] font-bold">
                              AJUSTADO
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {b.primary_booking_id && b.court_id && (
                        <button
                          data-testid={`edit-booking-${b.primary_booking_id}`}
                          onClick={() => setEditingBooking(b)}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 hover:border-[#CCFF00]/30 transition-colors"
                        >
                          <PencilSimple size={12} weight="bold" />
                          Ajustar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Manual ambient events (Parking / Terraza) */}
      <ManualAmbient />

      {editingBooking && (
        <OverrideModal
          booking={editingBooking}
          onClose={() => setEditingBooking(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
